import { ElementType } from "react";

import { Calendar } from "@UI/Components/Icons/Calendar";
import { ListCheck } from "@UI/Components/Icons/ListCheck";
import { UserIcon } from "@UI/Components/Icons/UserIcon";

import { IQuestionType } from "../types";

/** Step keys, in the order the user meets them. */
export const STEP_KEYS = {
  SERVICE_DETAILS: "service_details",
  TIME_AND_LOCATION: "time_and_location",
  /** Not in the config — the frontend appends it for logged-out users. */
  CONTACT_DETAILS: "contact_details",
} as const;

/**
 * The summary row icon per step. Keyed rather than positional: the config
 * decides how many steps there are and in what order, so an array index would
 * hand the wrong glyph to any category whose steps differ from painter's.
 *
 * A step key with no entry here renders no icon rather than throwing, so a new
 * config step degrades instead of breaking the panel.
 */
export const STEP_ICONS: Record<string, ElementType> = {
  [STEP_KEYS.SERVICE_DETAILS]: ListCheck,
  [STEP_KEYS.TIME_AND_LOCATION]: Calendar,
  [STEP_KEYS.CONTACT_DETAILS]: UserIcon,
};

/**
 * GrowthBook flag for the full flow on top of `task_creation`: logged-out
 * access, AI enrichment, editing, and the browser-side contact-info check.
 */
export const TASK_CREATION_FULL_FLAG = "task_creation_full";

/** The subcategory picker is a frontend screen, not a config one. */
export const SUBCATEGORY_SCREEN_KEY = "subcategory";

/**
 * `EMAIL` is the only one in the flow shape; `LOGIN` and `REGISTER` are
 * branches off it, chosen by whether the address has an account.
 */
export const AUTH_SCREEN_KEYS = {
  EMAIL: "auth",
  LOGIN: "login",
  REGISTER: "register",
} as const;

/** True for a screen reached *from* the email screen rather than beside it. */
export const isAuthBranchScreenKey = (screenKey?: string): boolean =>
  screenKey === AUTH_SCREEN_KEYS.LOGIN ||
  screenKey === AUTH_SCREEN_KEYS.REGISTER;

/** `EmailValidator.validate_email`'s pattern, so both agree on what is valid. */
export const EMAIL_PATTERN =
  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;

/**
 * Letters (any script, incl. accents), spaces, hyphens and apostrophes —
 * covers "Jean-Pierre" and "O'Brien" while still refusing digits and symbols
 * like the '@' an SSO provider can leave behind in a name field.
 */
export const NAME_PATTERN = /^[\p{L}\p{M}'\s-]+$/u;

/**
 * Mirrors `Authentication::MIN_PASSWORD_LENGTH`. Checked here so a password too
 * short to be anyone's never costs an attempt against the lockout.
 */
export const PASSWORD_MIN_LENGTH = 6;

/**
 * Questions the edit flow never asks again. Audience is set when the request
 * is published and changing it there is a separate action, not part of editing
 * the request itself. Its screen holds nothing else, so the shape's own
 * "every question hidden" rule drops the screen with it.
 */
export const EDIT_HIDDEN_QUESTION_KEYS: readonly string[] = ["audience"];

export const STORAGE_KEYS = {
  /** The draft's public token (`signed_id`). */
  TOKEN: "rt.taskCreation.token",
  /** Where to land on resume, so we need not wait on the draft fetch. */
  LAST_SCREEN: "rt.taskCreation.lastScreen",
  /**
   * Questions whose assistant-filled value the user has edited or whose banner
   * they dismissed. Sticky: once a key lands here the chip never comes back,
   * even if the value returns to what the assistant suggested.
   */
  DISMISSED_HINTS: "rt.taskCreation.dismissedPrefills",
} as const;

/**
 * `sessionStorage`, unlike the keys above: it must survive a refresh but only
 * for this visit — a shared computer must not still greet Blanche tomorrow.
 */
export const AUTH_IDENTITY_SESSION_KEY = "rt.taskCreation.authIdentity";

/**
 * The request just published, so its show page opens the success modal once —
 * consumed on arrival so a refresh does not bring it back.
 */
export const JUST_PUBLISHED_SESSION_KEY = "rt.taskCreation.justPublishedId";

/**
 * Description bounds, confirmed by product. The backend rejects with
 * `description_too_short` / `description_too_long` but publishes no numbers,
 * and `Task` carries no length validation, so we validate here to spare the
 * user a round trip.
 */
export const DESCRIPTION_LIMITS = {
  MIN_LENGTH: 20,
  MAX_LENGTH: 750,
} as const;

/**
 * Types the config can send. A type absent from the question registry renders
 * nothing rather than throwing, so an unknown type degrades to a skipped
 * question instead of a blank flow.
 */
export const SUPPORTED_QUESTION_TYPES: readonly IQuestionType[] = [
  "text",
  "integer",
  "float",
  "date",
  "address",
  "single_select",
  "multi_select",
  "picture_upload",
];

/**
 * The `p_category` themes this flow supports.
 *
 * Bricolage (4) is the only one so far, but naming the constant after DIY would
 * stop being true the day a second theme is added — hence a list. A visitor
 * arriving under any other theme is sent back to the category picker
 * (`DescriptionScreen`).
 */
export const ALLOWED_P_CATEGORY_IDS: readonly number[] = [4];

/**
 * The French `PCategory#name_fr` of each theme in `ALLOWED_P_CATEGORY_IDS`, the
 * names GA4 reports (see `buildTrackingParams`). The description screen submits
 * before any subcategory exists, so the draft cannot name the theme yet; the flow
 * knows it from the URL.
 */
export const P_CATEGORY_NAMES_FR: Readonly<Record<number, string>> = {
  4: "Bricolage",
};


/**
 * The category tree now comes from the live, unauthenticated
 * `/api/public/categories` endpoint (see `services/categories.ts`) — flip
 * this back to `true` only for local/test use against
 * `fixtures/bricolageCategories.json`.
 */
export const USE_MOCK_CATEGORY_TREE = false;

/**
 * `category` sent to `@Shared/services/photoAttachments` for every photo this
 * flow uploads — the description screen's strip and the photos screen alike.
 *
 * A `task_photos` row starts out unattached (`photo_attachable_id: nil`, see
 * `app/models/photo_attachment.rb`) and is linked to the draft later, which is
 * what lets the description screen upload before a draft exists at all. The
 * `api/public/photo_attachments` routes take it with no signed-in user.
 */
export const TASK_PHOTO_CATEGORY = "task_photos";

/**
 * What the description screen's uploader allows.
 *
 * The photos *screen* reads its own `constraints` off the question config, but
 * the description screen runs before any subcategory — and therefore any
 * config — exists, so its bounds are declared here. Both numbers mirror what
 * the backend and the configs already say: the extensions are
 * `PhotoAttachment::ALLOWED_EXTENSIONS["task_photos"]`, and every category
 * fixture declares the same `max_files`.
 */
export const DESCRIPTION_PHOTO_LIMITS = {
  MAX_FILES: 10,
  ALLOWED_EXTENSIONS: ["jpg", "jpeg", "png", "heic", "webp"],
} as const;

/**
 * The largest file either screen will upload, matching the portfolio photo
 * book (`usePhotoBookUpload.ts`), which is the only other place in the app
 * that gates on size.
 *
 * A flow-wide constant rather than part of `DESCRIPTION_PHOTO_LIMITS`: the
 * photos screen takes its file count and extensions from the question's
 * `constraints`, but those carry no size at all, and neither the presigned S3
 * PUT nor `PhotoAttachment` rejects on one — so this is the only gate a photo
 * meets, on both screens.
 */
export const MAX_PHOTO_FILE_SIZE_BYTES = 10 * 1024 * 1024;
